/**
 * 发单检测用剧本表解析。
 * 区别于气氛员匹配剧本：需要解析 A 列时间，并保留发单员与气氛员行；
 * C 列需标注内容类型（文本/图片/视频/表情包），供后续按时间间隔粗匹配。
 */

import { extractFeishuImageFileToken, formatFeishuCellValue } from "./feishuCellValue";
import {
  filterRoleColumnMerges,
  findRoleColumnMergeAnchorRow,
  type FeishuMergeRange,
} from "./feishuSheetMerges";
import {
  isScriptAtmosphereRole,
  SCRIPT_ATMOSPHERE_ROLE_PREFIX,
  SCRIPT_DISPATCHER_ROLE,
  SCRIPT_ROLE_COLUMN_INDEX,
} from "./feishuScriptSheet";

/** A 列时间 */
export const DISPATCH_SCRIPT_TIME_COLUMN_INDEX = 0;
/** C 列内容 */
export const DISPATCH_SCRIPT_CONTENT_COLUMN_INDEX = 2;

/** 发单侧角色（精确匹配；表内既有「发单号」也有「发单员」） */
export const DISPATCH_SCRIPT_DISPATCHER_ROLES = new Set([
  SCRIPT_DISPATCHER_ROLE,
  "发单员",
]);

export const DISPATCH_SCRIPT_CONTENT_TYPES = [
  "text",
  "image",
  "video",
  "sticker",
  "slot",
] as const;

export type DispatchScriptContentType = (typeof DISPATCH_SCRIPT_CONTENT_TYPES)[number];

export type DispatchScriptRoleKind = "dispatcher" | "atmosphere";

/** C 列插槽关键词 → 稳定枚举 */
export const DISPATCH_SCRIPT_SLOT_KINDS = [
  "points_screenshot",
  "points_red_packet_screenshot",
  "order_image",
  "video",
] as const;

export type DispatchScriptSlotKind = (typeof DISPATCH_SCRIPT_SLOT_KINDS)[number];

export const DISPATCH_SCRIPT_SLOT_LABELS: Record<DispatchScriptSlotKind, string> = {
  points_screenshot: "积分截图",
  points_red_packet_screenshot: "积分红包截图",
  order_image: "订单图",
  video: "视频槽位",
};

/** 需绑定参考图、用 dHash 匹配的关键词行（不再按时间窗口吞任意图片） */
export const DISPATCH_SCRIPT_REFERENCE_IMAGE_SLOTS = [
  "points_screenshot",
  "points_red_packet_screenshot",
  "order_image",
] as const;

export type DispatchScriptReferenceImageSlot =
  (typeof DISPATCH_SCRIPT_REFERENCE_IMAGE_SLOTS)[number];

export function isDispatchScriptReferenceImageSlot(
  slot: string
): slot is DispatchScriptReferenceImageSlot {
  return (DISPATCH_SCRIPT_REFERENCE_IMAGE_SLOTS as readonly string[]).includes(slot);
}

export function isDispatchScriptReferenceImageRow(row: { slots?: readonly string[] }): boolean {
  return (row.slots || []).some(isDispatchScriptReferenceImageSlot);
}

/**
 * 旧快照把积分/订单图记成 slot；现改为普通图片行，重抓/读路径用同一规则升级 contentTypes。
 */
export function upgradeDispatchScriptReferenceImageContentTypes(
  contentTypes: readonly string[],
  slots: readonly string[]
): DispatchScriptContentType[] {
  if (!slots.some(isDispatchScriptReferenceImageSlot)) {
    return DISPATCH_SCRIPT_CONTENT_TYPES.filter((type) => contentTypes.includes(type));
  }
  const next = new Set(contentTypes);
  next.add("image");
  const hasOtherKeywordSlots = slots.some(
    (slot) => !isDispatchScriptReferenceImageSlot(slot) && slot !== "video"
  );
  if (!hasOtherKeywordSlots) next.delete("slot");
  return DISPATCH_SCRIPT_CONTENT_TYPES.filter((type) => next.has(type));
}

/** 插槽识别用的原文关键词（按长度降序，避免短词误伤） */
export const DISPATCH_SCRIPT_SLOT_MARKERS: Array<{
  kind: DispatchScriptSlotKind;
  markers: string[];
}> = [
  { kind: "points_red_packet_screenshot", markers: ["积分红包截图"] },
  { kind: "points_screenshot", markers: ["积分截图"] },
  { kind: "order_image", markers: ["订单截图", "订单图"] },
];

export const DISPATCH_SCRIPT_CONTENT_TYPE_LABELS: Record<
  DispatchScriptContentType,
  string
> = {
  text: "文本",
  image: "图片",
  video: "视频",
  sticker: "表情包",
  slot: "插槽",
};

export const DISPATCH_SCRIPT_ROLE_KIND_LABELS: Record<DispatchScriptRoleKind, string> = {
  dispatcher: "发单侧",
  atmosphere: "气氛员",
};

export interface ParsedDispatchScriptTime {
  /** 原始 A 列文本 */
  raw: string;
  /** 是否由上方行继承 */
  inherited: boolean;
  /** 解析成功时：小时 0–23 */
  hour?: number;
  /** 解析成功时：分钟 0–59 */
  minute?: number;
  /** HH:mm */
  display?: string;
  /** 从当日 0 点起的分钟数，便于排序/区间比较 */
  totalMinutes?: number;
  /** 有原始值但无法按 3–4 位规则解析 */
  parseError?: string;
}

export interface ParsedDispatchScriptRow {
  rowIndex: number;
  rowNumber: number;
  time: ParsedDispatchScriptTime;
  role: string;
  roleKind: DispatchScriptRoleKind;
  roleIsInherited: boolean;
  /** C 列纯文本（不含 embed-image 产生的占位） */
  text: string;
  /** C 列嵌图 fileToken */
  imageFileToken?: string;
  /** 已识别的内容类型（可多选） */
  contentTypes: DispatchScriptContentType[];
  /** 命中的插槽标签；积分/订单图同时记入 image，匹配走 dHash */
  slots: DispatchScriptSlotKind[];
  /**
   * 无法归入已知类型的内容提示。
   * 有值表示本行存在未识别内容，不应当成已覆盖。
   */
  unrecognizedHints: string[];
}

export interface DispatchScriptSkipRow {
  rowIndex: number;
  rowNumber: number;
  reason: string;
  roleRaw: string;
  timeRaw: string;
  contentPreview: string;
}

export interface ParseDispatchScriptSheetResult {
  rows: ParsedDispatchScriptRow[];
  skipped: DispatchScriptSkipRow[];
  stats: {
    totalRows: number;
    keptRows: number;
    skippedRows: number;
    dispatcherRows: number;
    atmosphereRows: number;
    rowsWithTime: number;
    rowsWithInheritedTime: number;
    rowsWithTimeParseError: number;
    contentTypeCounts: Record<DispatchScriptContentType, number>;
    slotKindCounts: Record<DispatchScriptSlotKind, number>;
    rowsWithUnrecognized: number;
  };
}

function trimCell(value: unknown): string {
  return formatFeishuCellValue(value).trim();
}

export function isDispatchScriptDispatcherRole(role: string): boolean {
  return DISPATCH_SCRIPT_DISPATCHER_ROLES.has(role.trim());
}

export function isDispatchScriptVisibleRole(role: string): boolean {
  return isScriptAtmosphereRole(role) || isDispatchScriptDispatcherRole(role);
}

export function classifyDispatchScriptRoleKind(
  role: string
): DispatchScriptRoleKind | null {
  if (isDispatchScriptDispatcherRole(role)) return "dispatcher";
  if (isScriptAtmosphereRole(role)) return "atmosphere";
  return null;
}

/**
 * 解析 A 列时间：3–4 位数字，末两位为分钟，前面为小时。
 * 例：637 → 06:37；1003 → 10:03。
 * 不接受 1–2 位或非数字，避免静默猜错。
 */
export function parseDispatchScriptTimeRaw(rawInput: string): Omit<
  ParsedDispatchScriptTime,
  "inherited"
> {
  const raw = rawInput.trim();
  if (!raw) {
    return { raw: "" };
  }

  // 飞书偶发把数字渲染成带小数，如 "637.0"
  const normalized = raw.replace(/\.0+$/, "");
  if (!/^\d{3,4}$/.test(normalized)) {
    return {
      raw,
      parseError: `无法解析时间「${raw}」：仅支持 3–4 位数字（如 637、1003）`,
    };
  }

  const minute = Number(normalized.slice(-2));
  const hour = Number(normalized.slice(0, -2));

  if (!Number.isInteger(hour) || !Number.isInteger(minute)) {
    return { raw, parseError: `无法解析时间「${raw}」` };
  }
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return {
      raw,
      parseError: `时间「${raw}」超出有效范围（小时 0–23，分钟 0–59）`,
    };
  }

  const display = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  return {
    raw,
    hour,
    minute,
    display,
    totalMinutes: hour * 60 + minute,
  };
}

const IMAGE_TEXT_MARKERS = ["[图片]", "【图片】"];
const VIDEO_TEXT_MARKERS = ["[视频]", "【视频】"];
const STICKER_TEXT_MARKERS = ["[表情包]", "【表情包】", "[表情]", "【表情】"];

function containsAnyMarker(text: string, markers: string[]): boolean {
  return markers.some((marker) => text.includes(marker));
}

function stripMarkers(text: string, markers: string[]): string {
  let result = text;
  for (const marker of markers) {
    result = result.split(marker).join(" ");
  }
  return result.replace(/\s+/g, " ").trim();
}

/**
 * 识别 C 列内容类型。
 * 规则刻意收窄：只认 embed-image、明确括号标记、以及约定插槽关键词。
 * 「积分截图/积分红包截图/订单图」按普通图片处理（需绑定素材后 dHash 匹配），不再当 type_order 插槽。
 */
export function detectDispatchScriptContentTypes(input: {
  text: string;
  hasEmbedImage: boolean;
}): {
  contentTypes: DispatchScriptContentType[];
  remainingText: string;
  slots: DispatchScriptSlotKind[];
  unrecognizedHints: string[];
} {
  const contentTypes = new Set<DispatchScriptContentType>();
  const unrecognizedHints: string[] = [];
  const slots: DispatchScriptSlotKind[] = [];
  let remaining = input.text.trim();

  const mp4Match = remaining.match(/(?:^|\s)([^\s]+\.mp4)\s*$/i);
  if (mp4Match) {
    contentTypes.add("video");
    slots.push("video");
    remaining = remaining.slice(0, mp4Match.index).trim();
  }

  if (input.hasEmbedImage) {
    contentTypes.add("image");
  }

  if (containsAnyMarker(remaining, IMAGE_TEXT_MARKERS)) {
    contentTypes.add("image");
    remaining = stripMarkers(remaining, IMAGE_TEXT_MARKERS);
  }

  if (containsAnyMarker(remaining, VIDEO_TEXT_MARKERS)) {
    contentTypes.add("video");
    remaining = stripMarkers(remaining, VIDEO_TEXT_MARKERS);
  }

  if (containsAnyMarker(remaining, STICKER_TEXT_MARKERS)) {
    contentTypes.add("sticker");
    remaining = stripMarkers(remaining, STICKER_TEXT_MARKERS);
  }

  for (const slot of DISPATCH_SCRIPT_SLOT_MARKERS) {
    if (!containsAnyMarker(remaining, slot.markers)) continue;
    slots.push(slot.kind);
    remaining = stripMarkers(remaining, slot.markers);
    contentTypes.add("image");
  }

  if (remaining) {
    contentTypes.add("text");
  }

  // 仍含疑似媒体词但未套标准标记 → 明确提示缺口，不静默归入对应类型
  const suspiciousMatch = remaining.match(/表情包|发视频|发图片|发图|动图|视频|图片/);
  if (suspiciousMatch) {
    unrecognizedHints.push(
      `文案含「${suspiciousMatch[0]}」但未使用标准标记（[图片]/[视频]/[表情包]），未计入对应类型`
    );
  }

  if (contentTypes.size === 0 && !input.hasEmbedImage) {
    unrecognizedHints.push("C 列无文本且无嵌图，内容类型为空");
  }

  return {
    contentTypes: DISPATCH_SCRIPT_CONTENT_TYPES.filter((type) => contentTypes.has(type)),
    remainingText: remaining,
    slots,
    unrecognizedHints,
  };
}

function readRoleCell(input: {
  values: string[][];
  rawValues?: unknown[][];
  rowIndex: number;
}): { roleRaw: string; roleImageFileToken?: string } {
  const row = input.values[input.rowIndex] ?? [];
  const rawRow = input.rawValues?.[input.rowIndex] ?? [];
  const roleRawCell = rawRow[SCRIPT_ROLE_COLUMN_INDEX];
  const roleImageFileToken = extractFeishuImageFileToken(roleRawCell) ?? undefined;
  const roleRaw = roleImageFileToken ? "" : trimCell(row[SCRIPT_ROLE_COLUMN_INDEX]);
  return { roleRaw, roleImageFileToken };
}

function buildInheritedRoleState(
  values: string[][],
  rawValues: unknown[][] | undefined,
  roleColumnMerges: FeishuMergeRange[],
  rowIndex: number,
  currentRole: string
): { role: string; roleIsInherited: boolean; nextCurrentRole: string } {
  const bColumnMerges = filterRoleColumnMerges(roleColumnMerges);
  const mergeAnchorRow = findRoleColumnMergeAnchorRow(bColumnMerges, rowIndex);
  const roleCell =
    mergeAnchorRow !== undefined
      ? readRoleCell({ values, rawValues, rowIndex: mergeAnchorRow })
      : readRoleCell({ values, rawValues, rowIndex });
  const { roleRaw, roleImageFileToken } = roleCell;
  const isMergeAnchorRow = mergeAnchorRow !== undefined && mergeAnchorRow === rowIndex;
  const inRoleColumnMerge = mergeAnchorRow !== undefined;
  const roleIsMergeInherited =
    mergeAnchorRow !== undefined && mergeAnchorRow !== rowIndex;
  const useMergeApi = bColumnMerges.length > 0;
  const roleIsMergedDuplicate =
    !useMergeApi && Boolean(roleRaw && currentRole && roleRaw === currentRole);
  const roleIsInherited =
    roleIsMergeInherited ||
    roleIsMergedDuplicate ||
    (!useMergeApi && !roleRaw && !roleImageFileToken && Boolean(currentRole)) ||
    (useMergeApi &&
      !inRoleColumnMerge &&
      !isMergeAnchorRow &&
      !roleRaw &&
      !roleImageFileToken &&
      Boolean(currentRole));

  let nextCurrentRole = currentRole;
  if (roleRaw && !roleIsMergeInherited && !roleIsMergedDuplicate) {
    nextCurrentRole = roleRaw;
  } else if (roleIsMergeInherited && roleRaw) {
    nextCurrentRole = roleRaw;
  }

  return {
    role: roleRaw || nextCurrentRole,
    roleIsInherited,
    nextCurrentRole,
  };
}

function emptyContentTypeCounts(): Record<DispatchScriptContentType, number> {
  return { text: 0, image: 0, video: 0, sticker: 0, slot: 0 };
}

function emptySlotKindCounts(): Record<DispatchScriptSlotKind, number> {
  return {
    points_screenshot: 0,
    points_red_packet_screenshot: 0,
    order_image: 0,
    video: 0,
  };
}

/**
 * 解析发单检测剧本：
 * - A：时间（3–4 位数字）；空单元格向上继承，并标记 inherited
 * - B：角色（发单号/发单员 + 气氛员*）；合并/向上继承逻辑与气氛员剧本一致
 * - C：内容；识别文本、[图片]/[视频]/[表情包]/embed-image，以及「积分截图」「订单图」（普通图片，dHash）
 * 非目标角色或无 C 列内容的行进入 skipped，不丢弃信息。
 */
export function parseDispatchScriptSheetValues(
  values: string[][],
  rawValues?: unknown[][],
  roleColumnMerges: FeishuMergeRange[] = []
): ParseDispatchScriptSheetResult {
  const rows: ParsedDispatchScriptRow[] = [];
  const skipped: DispatchScriptSkipRow[] = [];
  let currentRole = "";
  let currentTime: ParsedDispatchScriptTime = { raw: "", inherited: false };

  for (let rowIndex = 0; rowIndex < values.length; rowIndex += 1) {
    const row = values[rowIndex] ?? [];
    const rawRow = rawValues?.[rowIndex] ?? [];
    const rowNumber = rowIndex + 1;

    const timeRaw = trimCell(row[DISPATCH_SCRIPT_TIME_COLUMN_INDEX]);
    let time: ParsedDispatchScriptTime;
    if (timeRaw) {
      const parsed = parseDispatchScriptTimeRaw(timeRaw);
      time = { ...parsed, inherited: false };
      if (parsed.display) {
        currentTime = time;
      } else {
        // 有值但解析失败：不继承旧时间，避免掩盖错误
        currentTime = { raw: timeRaw, inherited: false, parseError: parsed.parseError };
      }
    } else if (currentTime.display || currentTime.parseError) {
      time = { ...currentTime, inherited: true, raw: "" };
    } else {
      time = { raw: "", inherited: false };
    }

    const roleState = buildInheritedRoleState(
      values,
      rawValues,
      roleColumnMerges,
      rowIndex,
      currentRole
    );
    currentRole = roleState.nextCurrentRole;
    const role = roleState.role;
    const roleKind = classifyDispatchScriptRoleKind(role);

    const contentRawCell = rawRow[DISPATCH_SCRIPT_CONTENT_COLUMN_INDEX];
    const imageFileToken = extractFeishuImageFileToken(contentRawCell) ?? undefined;
    const contentText = imageFileToken
      ? ""
      : trimCell(row[DISPATCH_SCRIPT_CONTENT_COLUMN_INDEX]);
    // 若单元格既是复杂对象又含文本片段，仍尽量读 format 后的文本
    const textFromFormatted = trimCell(row[DISPATCH_SCRIPT_CONTENT_COLUMN_INDEX]);
    const text = imageFileToken
      ? textFromFormatted.replace(/^\[图片\]$/, "").trim()
      : contentText;

    const hasContent = Boolean(text.trim() || imageFileToken);
    if (!hasContent) {
      skipped.push({
        rowIndex,
        rowNumber,
        reason: "C 列无内容",
        roleRaw: role,
        timeRaw: time.raw || (time.inherited ? `(继承 ${time.display ?? "—"})` : ""),
        contentPreview: "",
      });
      continue;
    }

    if (!roleKind) {
      skipped.push({
        rowIndex,
        rowNumber,
        reason: role
          ? `角色「${role}」不是发单号/发单员或气氛员`
          : "B 列角色为空且无法向上继承",
        roleRaw: role,
        timeRaw: time.raw || (time.inherited ? `(继承 ${time.display ?? "—"})` : ""),
        contentPreview: text.slice(0, 80),
      });
      continue;
    }

    const detected = detectDispatchScriptContentTypes({
      text,
      hasEmbedImage: Boolean(imageFileToken),
    });

    rows.push({
      rowIndex,
      rowNumber,
      time,
      role,
      roleKind,
      roleIsInherited: roleState.roleIsInherited,
      text: detected.remainingText,
      imageFileToken,
      contentTypes: detected.contentTypes,
      slots: detected.slots,
      unrecognizedHints: detected.unrecognizedHints,
    });
  }

  const contentTypeCounts = emptyContentTypeCounts();
  const slotKindCounts = emptySlotKindCounts();
  for (const row of rows) {
    for (const type of row.contentTypes) {
      contentTypeCounts[type] += 1;
    }
    for (const slot of row.slots) {
      slotKindCounts[slot] += 1;
    }
  }

  return {
    rows,
    skipped,
    stats: {
      totalRows: values.length,
      keptRows: rows.length,
      skippedRows: skipped.length,
      dispatcherRows: rows.filter((row) => row.roleKind === "dispatcher").length,
      atmosphereRows: rows.filter((row) => row.roleKind === "atmosphere").length,
      rowsWithTime: rows.filter((row) => Boolean(row.time.display) && !row.time.inherited)
        .length,
      rowsWithInheritedTime: rows.filter(
        (row) => row.time.inherited && Boolean(row.time.display)
      ).length,
      rowsWithTimeParseError: rows.filter((row) => Boolean(row.time.parseError)).length,
      contentTypeCounts,
      slotKindCounts,
      rowsWithUnrecognized: rows.filter((row) => row.unrecognizedHints.length > 0).length,
    },
  };
}

export {
  SCRIPT_ATMOSPHERE_ROLE_PREFIX,
  SCRIPT_DISPATCHER_ROLE,
  SCRIPT_ROLE_COLUMN_INDEX,
};
